import type { MutableRefObject } from "react";

import { abortActiveWorkflowStream, reconcileSessionHitl } from "@/shared/builder-session";
import { setChatLive } from "@/features/chat/multi/history/chat-history";
import { setChatAwaitingInput } from "@/features/chat/multi/signals/chat-signals";
import type { BuilderContextValue } from "../builder-types";
import {
  asStructuredIntent,
  asWorkflowPlan,
  paintDraftSavedCard,
  rejectsAwaitingIntentDowngrade,
} from "../sse/chat-helpers";
import {
  fetchSessionSnapshot,
  markClientSessionGone,
  snapshotRunActive,
  type LiveSessionSnapshot,
} from "../restore-session";
import { shouldAutoResumeAfterRestore } from "./resume-autonomous-build";

export type SessionUiCatchUpResult = {
  restoredHitl: boolean;
  restoredPlan: boolean;
  restoredIntent: boolean;
  clearedStaleComposio: boolean;
  sessionGone: boolean;
  runActive: boolean;
  shouldResume: boolean;
};

const EMPTY: SessionUiCatchUpResult = {
  restoredHitl: false,
  restoredPlan: false,
  restoredIntent: false,
  clearedStaleComposio: false,
  sessionGone: false,
  runActive: false,
  shouldResume: false,
};

/** One in-flight GET per session at a lifecycle boundary. */
const boundaryInFlight = new Map<
  string,
  { promise: Promise<SessionUiCatchUpResult>; allowBusyUnlock: boolean }
>();

function snapshotHasFormHitl(snapshot: LiveSessionSnapshot): boolean {
  return Boolean(
    snapshot.pendingUserQuestion ||
    (Array.isArray(snapshot.pendingComposioExecApprovals) &&
      snapshot.pendingComposioExecApprovals.length > 0) ||
    (Array.isArray(snapshot.pendingRuntimeInputRequests) &&
      snapshot.pendingRuntimeInputRequests.length > 0) ||
    snapshot.pauseReason?.fields?.length,
  );
}

function localFormHitl(active: BuilderContextValue): boolean {
  return Boolean(
    active.pendingUserQuestion ||
    active.pendingComposioExecQueue.length > 0 ||
    active.pendingRuntimeInput,
  );
}

/**
 * Re-apply parked MCQ / Composio / runtime forms from the session document.
 * Used when transcript restored from local buffer (or tab switch) without HITL.
 */
export async function ensureSessionHitlFromServer(
  builderRef: MutableRefObject<BuilderContextValue>,
  sessionId: string,
  timeoutMs = 20_000,
): Promise<boolean> {
  const id = sessionId.trim();
  if (!id) return false;
  try {
    const response = await fetchSessionSnapshot(id, timeoutMs);
    if (builderRef.current.sessionId !== id || !response.ok) return false;
    const snapshot = (await response.json()) as LiveSessionSnapshot;
    if (builderRef.current.sessionId !== id) return false;
    const hitl = reconcileSessionHitl(builderRef.current, id, snapshot);
    setChatAwaitingInput(id, localFormHitl(builderRef.current));
    if (hitl.restored) builderRef.current.setBusy(false);
    return hitl.restored;
  } catch {
    return false;
  }
}

export type SyncSessionUiOptions = {
  /**
   * When true, idle snapshots may clear Working… (stream-end / hub resync).
   * When false, never yank busy mid-turn — only unlock if HITL was restored.
   */
  allowBusyUnlock?: boolean;
};

/**
 * Reconcile UI to the persisted session document.
 * SSE is the fast path; this is SoT recovery at lifecycle boundaries.
 */
export async function syncSessionUiFromServer(
  builderRef: MutableRefObject<BuilderContextValue>,
  sessionId: string,
  timeoutMs = 45_000,
  options?: SyncSessionUiOptions,
): Promise<SessionUiCatchUpResult> {
  const id = sessionId.trim();
  if (!id) return { ...EMPTY };
  const allowBusyUnlock = options?.allowBusyUnlock === true;

  try {
    const response = await fetchSessionSnapshot(id, timeoutMs);
    const active = builderRef.current;
    if (active.sessionId !== id) return { ...EMPTY };

    if (response.status === 404) {
      markClientSessionGone(active, id);
      abortActiveWorkflowStream(id);
      return { ...EMPTY, sessionGone: true, clearedStaleComposio: true };
    }

    if (!response.ok) return { ...EMPTY };

    const snapshot = (await response.json()) as LiveSessionSnapshot;
    if (builderRef.current.sessionId !== id) return { ...EMPTY };

    let restoredPlan = false;
    const plan = asWorkflowPlan(snapshot.plan);
    if (plan) {
      // Set plan state only — do not paintPlanCard here (that can clear busy / re-park).
      active.setPlan(plan);
      restoredPlan = Boolean(
        active.messages.some((message) => message.type === "plan") ||
        snapshot.status === "awaiting_approval",
      );
    }

    let restoredIntent = false;
    const intent = asStructuredIntent(snapshot.intent);
    if (intent && !rejectsAwaitingIntentDowngrade(active.intent, intent)) {
      // Same: setIntent only — paintIntentCard clears busy and broke post-confirm flow.
      active.setIntent(intent);
      restoredIntent = Boolean(
        active.messages.some((message) => message.type === "intent") ||
        snapshot.status === "awaiting_intent_approval",
      );
    }

    const hitl = reconcileSessionHitl(active, id, snapshot);
    const restoredHitl = hitl.restored;
    const clearedStaleComposio = hitl.clearedStaleComposio;
    if (restoredHitl) active.setBusy(false);
    setChatAwaitingInput(id, localFormHitl(active));

    if (Array.isArray(snapshot.nodes) && snapshot.nodes.length) {
      active.applyGraphEvent("graph-relayout", { nodes: snapshot.nodes });
    }
    if (Array.isArray(snapshot.edges) && snapshot.edges.length) {
      active.applyGraphEvent("edges-sync", {
        edges: snapshot.edges,
        nodes: snapshot.nodes,
      });
    }

    // Graph catch-up can mark testRunStale; SoT must win (else Passed → Draft changed).
    if (snapshot.lastTestRun) active.setLastTestRun(snapshot.lastTestRun);
    if (typeof snapshot.testRunStale === "boolean") {
      active.setTestRunStale(snapshot.testRunStale);
    }
    // FlowOps applyBuildSessionSnapshot — null clears a prior finalize failure.
    active.setLastFinalizeError(
      typeof snapshot.lastFinalizeError === "string"
        ? snapshot.lastFinalizeError.trim() || null
        : null,
    );

    if (
      !localFormHitl(active) &&
      !restoredHitl &&
      (snapshot.status === "testing" ||
        snapshot.status === "building" ||
        snapshot.status === "complete")
    ) {
      active.setPhase(snapshot.status);
    }

    const runActive = snapshotRunActive(snapshot) && String(snapshot.status ?? "") !== "complete";
    const hasPendingHitl = localFormHitl(active) || snapshotHasFormHitl(snapshot) || restoredHitl;
    const shouldResume =
      !runActive && shouldAutoResumeAfterRestore(String(snapshot.status ?? ""), hasPendingHitl);

    if (String(snapshot.status ?? "") === "complete") {
      active.setPhase("complete");
      active.setBusy(false);
      setChatLive(id, false);
      if ("markFinalized" in active && typeof active.markFinalized === "function") {
        active.markFinalized();
      }
      paintDraftSavedCard(active);
    } else if (runActive || shouldResume) {
      if (!hasPendingHitl) {
        active.setBusy(true);
        active.setBuildStalled(false);
        setChatLive(id, true);
      }
    } else if (hasPendingHitl) {
      active.setBusy(false);
      setChatLive(id, false);
    } else if (allowBusyUnlock || !active.busy) {
      active.setBusy(false);
      setChatLive(id, false);
      if (snapshot.status === "testing" || snapshot.status === "complete") {
        paintDraftSavedCard(active);
      }
    } else if (snapshot.status === "testing" || snapshot.status === "complete") {
      // Mid-turn focus/resync: latch Validate card without killing Working….
      paintDraftSavedCard(active);
    }

    return {
      restoredHitl,
      restoredPlan,
      restoredIntent,
      clearedStaleComposio,
      sessionGone: false,
      runActive,
      shouldResume,
    };
  } catch {
    return { ...EMPTY };
  }
}

/**
 * Single-flight reconcile at a lifecycle boundary (park / stream-end / hub resync).
 * Not for every SSE frame — only when the run reaches a state the UI must converge to.
 */
export function reconcileSessionAtBoundary(
  builderRef: MutableRefObject<BuilderContextValue>,
  sessionId: string,
  options?: SyncSessionUiOptions & { timeoutMs?: number },
): Promise<SessionUiCatchUpResult> {
  const id = sessionId.trim();
  if (!id) return Promise.resolve({ ...EMPTY });

  const timeoutMs = options?.timeoutMs ?? 45_000;
  const allowBusyUnlock = options?.allowBusyUnlock === true;

  const existing = boundaryInFlight.get(id);
  if (existing) {
    // Already unlocking (or caller doesn't need unlock) — share the same GET.
    if (existing.allowBusyUnlock || !allowBusyUnlock) return existing.promise;
    // In-flight was soft (no unlock); upgrade once after it settles if still busy.
    return existing.promise.then(async (result) => {
      if (result.restoredHitl || !builderRef.current.busy) return result;
      if (builderRef.current.sessionId !== id) return result;
      return reconcileSessionAtBoundary(builderRef, id, {
        allowBusyUnlock: true,
        timeoutMs,
      });
    });
  }

  const entry = {
    allowBusyUnlock,
    promise: null as unknown as Promise<SessionUiCatchUpResult>,
  };
  entry.promise = syncSessionUiFromServer(builderRef, id, timeoutMs, {
    allowBusyUnlock,
  }).finally(() => {
    if (boundaryInFlight.get(id) === entry) boundaryInFlight.delete(id);
  });
  boundaryInFlight.set(id, entry);
  return entry.promise;
}

/** Test helper. */
export function resetSessionBoundaryReconcileForTests(): void {
  boundaryInFlight.clear();
}
